/**
 * Crystal Singing Bowl Multi-Player
 * Healing Sounds - V5
 */

(function() {
  'use strict';

  // Icons SVG
  const PLAY_ICON = '<path d="M8 5v14l11-7z"/>';
  const PAUSE_ICON = '<path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z"/>';

  class CrystalBowlPlayer {
    constructor(container) {
      this.container = container;
      this.notePlayers = container.querySelectorAll('.hs-note-player');
      this.freqButtons = container.querySelectorAll('.hs-freq-toggle__btn');
      this.stopAllBtn = container.querySelector('.hs-stop-all__btn');
      
      this.currentFreq = '432';
      this.audioInstances = new Map();
      
      this.init();
    }

    init() {
      this.bindFrequencyToggle();
      this.bindNotePlayers();
      this.bindStopAll();
    }

    formatTime(seconds) {
      if (isNaN(seconds)) return '0:00';
      const mins = Math.floor(seconds / 60);
      const secs = Math.floor(seconds % 60);
      return `${mins}:${String(secs).padStart(2, '0')}`;
    }

    bindFrequencyToggle() {
      this.freqButtons.forEach(btn => {
        btn.addEventListener('click', () => {
          this.currentFreq = btn.dataset.freq;
          this.freqButtons.forEach(b => b.classList.toggle('active', b === btn));
          this.stopAll();
        });
      });
    }

    bindNotePlayers() {
      this.notePlayers.forEach(np => {
        const playBtn = np.querySelector('.hs-note-player__play');
        const volumeSlider = np.querySelector('.hs-note-player__volume-slider');
        const progressEl = np.querySelector('.hs-note-player__progress');

        // Play/Pause
        playBtn.addEventListener('click', () => this.togglePlay(np));

        // Volume
        if (volumeSlider) {
          volumeSlider.addEventListener('input', (e) => {
            const audio = this.audioInstances.get(np.dataset.note);
            if (audio) {
              audio.volume = e.target.value / 100;
            }
          });
        }

        // Seek
        if (progressEl) {
          progressEl.addEventListener('click', (e) => {
            const audio = this.audioInstances.get(np.dataset.note);
            if (audio && audio.duration) {
              const rect = progressEl.getBoundingClientRect();
              const percent = (e.clientX - rect.left) / rect.width;
              audio.currentTime = percent * audio.duration;
            }
          });
        }
      });
    }

    bindStopAll() {
      if (this.stopAllBtn) {
        this.stopAllBtn.addEventListener('click', () => this.stopAll());
      }
    }

    togglePlay(np) {
      const note = np.dataset.note;
      const playBtn = np.querySelector('.hs-note-player__play');
      const progressBar = np.querySelector('.hs-note-player__progress-bar');
      const timeDisplay = np.querySelector('.hs-note-player__time');
      const volumeSlider = np.querySelector('.hs-note-player__volume-slider');

      if (this.audioInstances.has(note)) {
        const audio = this.audioInstances.get(note);
        
        if (audio.paused) {
          audio.play();
          np.classList.add('playing');
          playBtn.classList.add('playing');
          playBtn.innerHTML = `<svg viewBox="0 0 24 24">${PAUSE_ICON}</svg>`;
        } else {
          audio.pause();
          np.classList.remove('playing');
          playBtn.classList.remove('playing');
          playBtn.innerHTML = `<svg viewBox="0 0 24 24">${PLAY_ICON}</svg>`;
        }
      } else {
        // Create new audio instance
        const srcKey = `src${this.currentFreq}`;
        const src = np.dataset[srcKey];
        
        if (!src || src.includes('YOUR_')) {
          console.warn(`No audio source for note ${note} at ${this.currentFreq}Hz`);
          return;
        }

        const audio = new Audio(src);
        audio.volume = volumeSlider ? volumeSlider.value / 100 : 0.7;
        this.audioInstances.set(note, audio);

        // Events
        audio.addEventListener('loadedmetadata', () => {
          if (timeDisplay) {
            timeDisplay.textContent = `0:00 / ${this.formatTime(audio.duration)}`;
          }
        });

        audio.addEventListener('timeupdate', () => {
          if (progressBar && audio.duration) {
            progressBar.style.width = `${(audio.currentTime / audio.duration) * 100}%`;
          }
          if (timeDisplay) {
            timeDisplay.textContent = `${this.formatTime(audio.currentTime)} / ${this.formatTime(audio.duration)}`;
          }
        });

        audio.addEventListener('ended', () => {
          np.classList.remove('playing');
          playBtn.classList.remove('playing');
          playBtn.innerHTML = `<svg viewBox="0 0 24 24">${PLAY_ICON}</svg>`;
          if (progressBar) progressBar.style.width = '0%';
          this.audioInstances.delete(note);
        });

        audio.addEventListener('error', (e) => {
          console.error(`Error loading audio for note ${note}:`, e);
          np.classList.remove('playing');
          playBtn.classList.remove('playing');
          playBtn.innerHTML = `<svg viewBox="0 0 24 24">${PLAY_ICON}</svg>`;
        });

        // Play
        audio.play().catch(e => {
          console.warn('Autoplay blocked:', e);
        });
        
        np.classList.add('playing');
        playBtn.classList.add('playing');
        playBtn.innerHTML = `<svg viewBox="0 0 24 24">${PAUSE_ICON}</svg>`;
      }
    }

    stopAll() {
      this.audioInstances.forEach((audio, note) => {
        audio.pause();
        audio.currentTime = 0;
      });
      this.audioInstances.clear();

      this.notePlayers.forEach(np => {
        const playBtn = np.querySelector('.hs-note-player__play');
        const progressBar = np.querySelector('.hs-note-player__progress-bar');
        const timeDisplay = np.querySelector('.hs-note-player__time');

        np.classList.remove('playing');
        playBtn.classList.remove('playing');
        playBtn.innerHTML = `<svg viewBox="0 0 24 24">${PLAY_ICON}</svg>`;
        if (progressBar) progressBar.style.width = '0%';
        if (timeDisplay) timeDisplay.textContent = '0:00 / 0:00';
      });
    }
  }

  // Initialize all players on page
  function initPlayers() {
    document.querySelectorAll('.hs-multi-player').forEach(container => {
      if (!container.dataset.initialized) {
        new CrystalBowlPlayer(container);
        container.dataset.initialized = 'true';
      }
    });
  }

  // Init on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initPlayers);
  } else {
    initPlayers();
  }

  // Re-init on Shopify section load (for theme editor)
  document.addEventListener('shopify:section:load', initPlayers);

  // Export for external use
  window.CrystalBowlPlayer = CrystalBowlPlayer;
})();

